import { ActionBar, Checkbox, Input, Select, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { useLoopStore } from '../store'
import type { LoopConfigurationTabInput } from './types'

export const LoopConfigurationTab = () => {
	const { organizationId: _organizationId, loopId } = useParams<{
		organizationId: string
		loopId: string
	}>()
	const navigate = useNavigate()
	const toast = useToast()
	const { loop, useEditLoop } = useLoopStore()
	const { editLoop, isLoading: isEditing } = useEditLoop(loop?.id)

	const {
		handleSubmit,
		control,
		watch,
		formState: { errors },
		reset,
	} = useForm<LoopConfigurationTabInput>({
		defaultValues: {
			textQuestionEnabled: loop?.textQuestionEnabled ?? true,
			ratingQuestionEnabled: loop?.ratingQuestionEnabled ?? true,
			textQuestionLabel: loop?.textQuestionLabel || null,
			ratingQuestionLabel: loop?.ratingQuestionLabel || null,
			ratingScale: loop?.ratingScale || 5,
		},
	})

	const textQuestionEnabled = watch('textQuestionEnabled')
	const ratingQuestionEnabled = watch('ratingQuestionEnabled')

	useEffect(() => {
		if (loop) {
			reset({
				textQuestionEnabled: loop.textQuestionEnabled ?? true,
				ratingQuestionEnabled: loop.ratingQuestionEnabled ?? true,
				textQuestionLabel: loop.textQuestionLabel || null,
				ratingQuestionLabel: loop.ratingQuestionLabel || null,
				ratingScale: loop.ratingScale || 5,
			})
		}
	}, [loop, reset])

	const handleSubmitInternal = async (data: LoopConfigurationTabInput) => {
		if (!loop?.id) {
			toast.showToast('Please create the loop first (General tab)', 'error')
			return
		}

		// Validate that at least one question type is enabled
		if (!data.textQuestionEnabled && !data.ratingQuestionEnabled) {
			toast.showToast('At least one question type must be enabled', 'error')
			return
		}

		await editLoop({
			textQuestionEnabled: data.textQuestionEnabled,
			ratingQuestionEnabled: data.ratingQuestionEnabled,
			textQuestionLabel: data.textQuestionEnabled ? data.textQuestionLabel : null,
			ratingQuestionLabel: data.ratingQuestionEnabled ? data.ratingQuestionLabel : null,
			ratingScale: data.ratingQuestionEnabled ? data.ratingScale : null,
		})
	}

	const ratingScaleOptions = [
		{ value: '5', label: '5-point scale (1-5)' },
		{ value: '10', label: '10-point scale (1-10)' },
	]

	const isPending = isEditing

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 gap-8 px-6 max-w-4xl'>
				{/* Text Question Section */}
				<div className='space-y-4 p-6 bg-gray-50 rounded-lg'>
					<div className='flex items-start gap-3'>
						<Controller
							name='textQuestionEnabled'
							control={control}
							render={({ field }) => (
								<Checkbox id='textQuestionEnabled' checked={field.value} onChange={field.onChange} />
							)}
						/>
						<div className='flex-1'>
							<label htmlFor='textQuestionEnabled'>
								<Text variant='small' weight='semibold'>
									Enable Text Question
								</Text>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									Allow participants to provide open-ended text responses
								</Text>
							</label>
						</div>
					</div>

					{textQuestionEnabled && (
						<div className='ml-8'>
							<Controller
								name='textQuestionLabel'
								control={control}
								rules={{
									required: textQuestionEnabled ? 'Text question label is required' : false,
								}}
								render={({ field }) => (
									<Input
										label='Text Question Label'
										placeholder='e.g., What went well this week?'
										value={field.value || ''}
										onChange={field.onChange}
										onBlur={field.onBlur}
										variant={errors.textQuestionLabel ? 'error' : 'default'}
										hint={errors.textQuestionLabel?.message as string}
										isRequired={textQuestionEnabled}
									/>
								)}
							/>
						</div>
					)}
				</div>

				{/* Rating Question Section */}
				<div className='space-y-4 p-6 bg-gray-50 rounded-lg'>
					<div className='flex items-start gap-3'>
						<Controller
							name='ratingQuestionEnabled'
							control={control}
							render={({ field }) => (
								<Checkbox id='ratingQuestionEnabled' checked={field.value} onChange={field.onChange} />
							)}
						/>
						<div className='flex-1'>
							<label htmlFor='ratingQuestionEnabled'>
								<Text variant='small' weight='semibold'>
									Enable Rating Question
								</Text>
								<Text variant='xsmall' className='text-gray-600 mt-1'>
									Allow participants to provide a numerical rating
								</Text>
							</label>
						</div>
					</div>

					{ratingQuestionEnabled && (
						<div className='ml-8 space-y-4'>
							<Controller
								name='ratingQuestionLabel'
								control={control}
								rules={{
									required: ratingQuestionEnabled ? 'Rating question label is required' : false,
								}}
								render={({ field }) => (
									<Input
										label='Rating Question Label'
										placeholder='e.g., How satisfied are you with team collaboration?'
										value={field.value || ''}
										onChange={field.onChange}
										onBlur={field.onBlur}
										variant={errors.ratingQuestionLabel ? 'error' : 'default'}
										hint={errors.ratingQuestionLabel?.message as string}
										isRequired={ratingQuestionEnabled}
									/>
								)}
							/>

							<Controller
								name='ratingScale'
								control={control}
								rules={{
									required: ratingQuestionEnabled ? 'Rating scale is required' : false,
								}}
								render={({ field }) => (
									<Select
										label='Rating Scale'
										options={ratingScaleOptions}
										value={field.value?.toString() || '5'}
										onChange={(value) => field.onChange(Number.parseInt(value, 10))}
										variant={errors.ratingScale ? 'error' : 'default'}
										hint={errors.ratingScale?.message || 'Choose the rating scale for responses'}
										isRequired={ratingQuestionEnabled}
									/>
								)}
							/>
						</div>
					)}
				</div>

				{/* Validation Message */}
				{!textQuestionEnabled && !ratingQuestionEnabled && (
					<div className='bg-yellow-50 p-4 rounded-md'>
						<Text variant='xsmall' className='text-yellow-800'>
							<strong>Note:</strong> At least one question type must be enabled
						</Text>
					</div>
				)}
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Back',
						onClick: () => navigate(`/loops/${loopId}/participants`),
					},
					{
						variant: 'primary' as const,
						label: 'Save & Continue',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
		</form>
	)
}
